import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';

const SPACING = {
  footerPaddingVertical: 24,
  footerPaddingHorizontalDesktop: 24,
  footerPaddingHorizontalMobile: 16,
  sectionGap: 16,
  innerMaxWidth: 800,
  fontSize: 12,
  lineHeight: 1.7,
  navLinkPaddingVertical: 6,
  navLinkPaddingHorizontal: 4,
  navSeparatorMargin: 8,
};

const buildColors = (theme) => {
  const t = getThemeColors(theme);
  return {
    background: t.backgroundSecondary,
    border: t.borderPrimary,
    text: t.textFaint,
    link: t.accentPrimary,
    separator: t.borderPrimary,
  };
};

// Issues 3 & 4: static style objects at module scope; no textDecoration on innerStyle/sectionStyle
const innerStyle = {
  maxWidth: SPACING.innerMaxWidth,
  margin: '0 auto',
};

const sectionStyle = {
  marginBottom: SPACING.sectionGap,
};

// Issue 2: NAV_LINKS at module scope — does not depend on props, state, or context
const NAV_LINKS = [
  { label: 'Privacy Policy', to: '/privacy' },
  { label: 'Terms of Service', to: '/terms' },
  { label: 'Legal Notices', to: '/legal' },
  { label: 'Fan Content Policy', href: 'https://company.wizards.com/en/legal/fancontentpolicy' },
];

// Issue 1 & 5: helper components declared before Footer; renamed to FooterNavLink to avoid
// shadowing React Router's NavLink if it is ever imported in this module
const FooterNavLink = ({ to, href, children, linkStyle }) => {
  const [hovered, setHovered] = useState(false);
  const style = {
    ...linkStyle,
    textDecoration: hovered ? 'underline' : 'none',
    padding: `${SPACING.navLinkPaddingVertical}px ${SPACING.navLinkPaddingHorizontal}px`,
  };

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        style={style}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      to={to}
      style={style}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </Link>
  );
};

// Issue 1: InlineLink declared before Footer
const InlineLink = ({ href, style, children }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      style={{ ...style, textDecoration: hovered ? 'underline' : 'none' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </a>
  );
};

const Footer = () => {
  const { preferences } = usePreferences();
  const COLORS = buildColors(preferences.theme);

  const footerStyle = {
    backgroundColor: COLORS.background,
    borderTop: `1px solid ${COLORS.border}`,
    padding: `${SPACING.footerPaddingVertical}px ${isMobile ? SPACING.footerPaddingHorizontalMobile : SPACING.footerPaddingHorizontalDesktop}px`,
    fontSize: SPACING.fontSize,
    lineHeight: SPACING.lineHeight,
    color: COLORS.text,
    textAlign: 'center',
  };

  // Issue 4: textDecoration removed — InlineLink manages its own hover-driven textDecoration
  const inlineLinkStyle = {
    color: COLORS.link,
  };

  const navLinkStyle = {
    color: COLORS.link,
  };

  const separatorStyle = {
    margin: `0 ${SPACING.navSeparatorMargin}px`,
    color: COLORS.separator,
    userSelect: 'none',
  };

  return (
    <footer style={footerStyle}>
      <div style={innerStyle}>
        <div style={sectionStyle}>
          CollectNDeck is unofficial Fan Content permitted under the Fan Content Policy. Not approved/endorsed by Wizards. Portions of the materials used are property of Wizards of the Coast. ©Wizards of the Coast LLC.
        </div>
        <div style={sectionStyle}>
          Card data and prices provided by{' '}
          <InlineLink href="https://scryfall.com" style={inlineLinkStyle}>Scryfall</InlineLink>
          . Card artwork copyright belongs to the respective artists.
        </div>
        <div>
          {NAV_LINKS.map((link, i) => (
            <React.Fragment key={link.label}>
              <FooterNavLink to={link.to} href={link.href} linkStyle={navLinkStyle}>
                {link.label}
              </FooterNavLink>
              {i < NAV_LINKS.length - 1 && (
                <span style={separatorStyle} aria-hidden="true">|</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
